import { useAtom } from 'jotai';
import { useCallback } from 'react';
import {
  openDialogAtom,
  closeDialogAtom,
  ConfirmationDialogState,
  DialogSentiment,
} from '../atoms/confirmationDialogAtoms';

export interface ConfirmationDialogOptions {
  title: string;
  message: string;
  sentiment?: DialogSentiment;
  confirmText?: string;
  cancelText?: string;
  onCancel?: () => void | Promise<void>;
}

export const useConfirmationDialog = () => {
  const [, openDialog] = useAtom(openDialogAtom);
  const [, closeDialog] = useAtom(closeDialogAtom);

  const showConfirmation = useCallback(
    (
      options: ConfirmationDialogOptions,
      onConfirm: () => void | Promise<void>,
    ) => {
      const dialogState: Partial<ConfirmationDialogState> = {
        title: options.title,
        message: options.message,
        sentiment: options.sentiment || 'info',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        onConfirm,
        onCancel: options.onCancel,
        isLoading: false,
      };

      openDialog(dialogState);
    },
    [openDialog]
  );

  // Convenience methods for different sentiments
  const showSuccessConfirmation = useCallback(
    (
      title: string,
      message: string,
      onConfirm: () => void | Promise<void>,
      options?: Partial<ConfirmationDialogOptions>
    ) => {
      showConfirmation(
        {
          title,
          message,
          sentiment: 'success',
          confirmText: options?.confirmText || 'Accept',
          cancelText: options?.cancelText || 'Cancel',
          onCancel: options?.onCancel,
        },
        onConfirm
      );
    },
    [showConfirmation]
  );

  const showWarningConfirmation = useCallback(
    (
      title: string,
      message: string,
      onConfirm: () => void | Promise<void>,
      options?: Partial<ConfirmationDialogOptions>
    ) => {
      showConfirmation(
        {
          title,
          message,
          sentiment: 'warning',
          confirmText: options?.confirmText || 'Continue',
          cancelText: options?.cancelText || 'Cancel',
          onCancel: options?.onCancel,
        },
        onConfirm
      );
    },
    [showConfirmation]
  );

  const showDangerConfirmation = useCallback(
    (
      title: string,
      message: string,
      onConfirm: () => void | Promise<void>,
      options?: Partial<ConfirmationDialogOptions>
    ) => {
      showConfirmation(
        {
          title,
          message,
          sentiment: 'danger',
          confirmText: options?.confirmText || 'Delete',
          cancelText: options?.cancelText || 'Cancel',
          onCancel: options?.onCancel,
        },
        onConfirm
      );
    },
    [showConfirmation]
  );

  const showInfoConfirmation = useCallback(
    (
      title: string,
      message: string,
      onConfirm: () => void | Promise<void>,
      options?: Partial<ConfirmationDialogOptions>
    ) => {
      showConfirmation(
        {
          title,
          message,
          sentiment: 'info',
          confirmText: options?.confirmText || 'OK',
          cancelText: options?.cancelText || 'Cancel',
          onCancel: options?.onCancel,
        },
        onConfirm
      );
    },
    [showConfirmation]
  );

  return {
    showConfirmation,
    showSuccessConfirmation,
    showWarningConfirmation,
    showDangerConfirmation,
    showInfoConfirmation,
    closeDialog,
  };
};
